import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import api from "@/api/client";
import { toast } from "sonner";

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function Field({ label, hint, children, span }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className="mb-1.5 block font-mono text-xs uppercase tracking-widest text-slate-400">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-600">{hint}</p>}
    </div>
  );
}

const inputCls = "w-full border border-white/10 bg-surface px-4 py-3 text-white placeholder:text-slate-600 focus:border-volt focus:outline-none";

function OptionGroupsEditor({ groups, onChange }) {
  const setGroup = (i, g) => onChange(groups.map((cur, idx) => (idx === i ? g : cur)));

  return (
    <div className="sm:col-span-2" data-testid="option-groups-editor">
      <label className="mb-1.5 block font-mono text-xs uppercase tracking-widest text-slate-400">Options Buyers Pick (e.g. Bike, Color, Style)</label>
      <p className="mb-3 text-xs text-slate-600">Each option can add to the price. Buyers must pick one per group before adding to cart.</p>
      {groups.map((g, i) => (
        <div key={i} className="mb-3 border border-white/10 bg-obsidian p-3" data-testid={`option-group-${i}`}>
          <div className="mb-2 flex items-center gap-2">
            <input
              type="text"
              placeholder="Group label (e.g. Bike)"
              value={g.label}
              onChange={(e) => setGroup(i, { ...g, label: e.target.value })}
              className={inputCls}
              data-testid={`option-group-label-${i}`}
            />
            <button type="button" onClick={() => onChange(groups.filter((_, idx) => idx !== i))} className="p-2 text-slate-500 hover:text-red-400" data-testid={`option-group-delete-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          {g.choices.map((c, j) => (
            <div key={j} className="mb-2 flex items-center gap-2">
              <input
                type="text"
                placeholder="Option name (e.g. Ultra Bee)"
                value={c.name}
                onChange={(e) => setGroup(i, { ...g, choices: g.choices.map((cc, jj) => (jj === j ? { ...cc, name: e.target.value } : cc)) })}
                className={inputCls}
                data-testid={`option-name-${i}-${j}`}
              />
              <div className="flex items-center gap-1 border border-white/10 bg-surface px-3 py-3">
                <span className="font-mono text-xs text-slate-500">+$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={c.price_modifier}
                  onChange={(e) => setGroup(i, { ...g, choices: g.choices.map((cc, jj) => (jj === j ? { ...cc, price_modifier: parseFloat(e.target.value) || 0 } : cc)) })}
                  className="w-20 bg-transparent text-white focus:outline-none"
                  data-testid={`option-price-${i}-${j}`}
                />
              </div>
              <button type="button" onClick={() => setGroup(i, { ...g, choices: g.choices.filter((_, jj) => jj !== j) })} className="p-2 text-slate-500 hover:text-red-400" data-testid={`option-delete-${i}-${j}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setGroup(i, { ...g, choices: [...g.choices, { name: "", price_modifier: 0 }] })}
            className="flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-volt hover:text-white"
            data-testid={`option-add-${i}`}
          >
            <Plus className="h-3 w-3" /> Add option
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...groups, { label: "", choices: [{ name: "", price_modifier: 0 }] }])}
        className="flex items-center gap-1 border border-dashed border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-widest text-slate-400 hover:border-volt hover:text-volt"
        data-testid="add-option-group"
      >
        <Plus className="h-3 w-3" /> Add option group
      </button>
    </div>
  );
}

function GalleryEditor({ images, onChange }) {
  const [uploading, setUploading] = useState(false);

  const uploadMany = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        onChange((prev) => [...prev, data.url]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {images.map((url, i) => (
          <div key={i} className="relative h-20 w-20 border border-white/10" data-testid={`gallery-image-${i}`}>
            <img src={url} alt={`Extra ${i + 1}`} className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(images.filter((_, idx) => idx !== i))}
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white"
              data-testid={`gallery-remove-${i}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label className={`flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-white/20 text-slate-400 transition-colors hover:border-volt hover:text-volt ${uploading ? "opacity-50" : ""}`} data-testid="gallery-upload">
          <Plus className="h-5 w-5" />
          <span className="font-mono text-[10px] uppercase">{uploading ? "Uploading" : "Add"}</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={uploadMany} disabled={uploading} />
        </label>
      </div>
      <p className="mt-2 text-xs text-slate-600">Select several photos at once — they show as the gallery under the main photo on the product page.</p>
    </div>
  );
}

export function ProductForm({ editing, setEditing, onSave, onClose, itemCats, fitments }) {
  const groups = editing.option_groups?.length
    ? editing.option_groups
    : (editing.options?.length ? [{ label: editing.options_label || "Option", choices: editing.options.map((o) => ({ name: typeof o === "object" ? o.name : o, price_modifier: typeof o === "object" ? o.price_modifier || 0 : 0 })) }] : []);

  const setGroups = (g) => setEditing({ ...editing, option_groups: g, options: [], options_label: "" });

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((cur) => ({ ...cur, image_url: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="product-form-modal">
      <form onSubmit={onSave} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto border border-white/10 bg-[#0B0F17] p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold uppercase text-white">{editing?.id ? "Edit Product" : "New Product"}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-form">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Product Name">
            <input type="text" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })} className={inputCls} placeholder="e.g. EBMX X-9000 Controller" data-testid="form-name" />
          </Field>
          <Field label="Category">
            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inputCls} data-testid="form-category">
              {itemCats.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Price ($)" hint="Base price. Options below can add on top.">
            <input type="number" step="0.01" min="0" required value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} className={inputCls} placeholder="e.g. 1164.00" data-testid="form-price" />
          </Field>
          <Field label="Stock (how many you have)">
            <input type="number" min="0" required value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: parseInt(e.target.value) || 0 })} className={inputCls} placeholder="e.g. 10" data-testid="form-stock" />
          </Field>
          <Field label="Badge (optional)" span hint='Shows a tag on the product card, e.g. "Best Seller". Leave blank for none.'>
            <input type="text" value={editing.badge} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} className={inputCls} placeholder="Leave blank for no badge" data-testid="form-badge" />
          </Field>
          <Field label="Main Photo" span>
            <input type="text" required value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className={inputCls} placeholder="Image URL, or upload below" data-testid="form-image" />
            <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 py-2 font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors hover:border-volt hover:text-volt" data-testid="form-image-upload">
              Upload photo from device
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} />
            </label>
          </Field>
          <Field label="Extra Photos (optional)" span>
            <GalleryEditor images={editing.images || []} onChange={(imgs) => setEditing((cur) => ({ ...cur, images: typeof imgs === "function" ? imgs(cur.images || []) : imgs }))} />
          </Field>
          <Field label="Description" span>
            <textarea required rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} placeholder="What is it, what's included, what makes it good" data-testid="form-description" />
          </Field>
          <OptionGroupsEditor groups={groups} onChange={setGroups} />
          <Field label="Fits These Bikes (compatibility badges)" span>
            <div className="flex flex-wrap gap-2">
              {fitments.map((f) => (
                <label key={f} className="flex items-center gap-2 border border-white/10 bg-surface px-3 py-2" data-testid={`form-fitment-${f.toLowerCase().replace(/\s+/g, "-")}`}>
                  <input
                    type="checkbox"
                    checked={editing.compatibility.includes(f)}
                    onChange={(e) => {
                      const comp = e.target.checked ? [...editing.compatibility, f] : editing.compatibility.filter((c) => c !== f);
                      setEditing({ ...editing, compatibility: comp });
                    }}
                    className="accent-[#00A3FF]"
                  />
                  <span className="font-mono text-xs uppercase text-slate-300">{f}</span>
                </label>
              ))}
            </div>
          </Field>
          <label className="flex items-center gap-2 sm:col-span-2" data-testid="form-featured-label">
            <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#00A3FF]" data-testid="form-featured" />
            <span className="font-mono text-xs uppercase tracking-widest text-slate-300">Show in "Top Shelf Parts" on the homepage</span>
          </label>
        </div>
        <div className="mt-6 flex gap-4">
          <button type="button" onClick={onClose} className="flex-1 border border-white/10 py-3 font-mono text-sm uppercase text-slate-400 hover:text-white" data-testid="form-cancel">
            Cancel
          </button>
          <button type="submit" className="flex-1 bg-volt py-3 font-mono text-sm font-bold uppercase text-obsidian hover:bg-white" data-testid="form-save">
            Save Product
          </button>
        </div>
      </form>
    </div>
  );
}
